import { Box, Grid, Table, TableBody, TableCell, TableRow, Theme, Typography } from '@mui/material';
import { seoSegmentationListingPage_seoSegmentationStats_SEOSegmentationStatsSuccess_caregiverStats as SEOSegmentationCaregiverStats } from '@/__generated__/seoSegmentationListingPage';
import formatNumToString from '@/utils/formatNumToString';

const classes = {
  careByNumContainer: (theme: Theme) => ({
    margin: theme.spacing(4, 0),
  }),
  tableBody: (theme: Theme) => ({
    [theme.breakpoints.up('sm')]: {
      display: 'flex',
      justifyContent: 'center',
    },
  }),
  careByNumText: (theme: Theme) => ({
    marginBottom: theme.spacing(3.25),
    lineHeight: '30px',
    fontWeight: 400,
    textAlign: 'center',

    [theme.breakpoints.up('sm')]: {
      textAlign: 'left',
    },
  }),
  statRow: (theme: Theme) => ({
    width: 'calc(50% - 1px)',
    display: 'inline-block',

    [theme.breakpoints.up('sm')]: {
      width: 'calc(25% - 1px)',
    },
  }),
  statCell: (theme: Theme) => ({
    width: '100%',
    height: '70px',
    display: 'inline-block',
    textAlign: 'center',
    padding: 0,
    borderBottom: 'none',
    borderRight: `1px solid ${theme.palette.care.grey[300]}`,
  }),
  avgExpStat: (theme: Theme) => ({
    borderRight: 'none',
    borderBottom: `1px solid ${theme.palette.care.grey[300]}`,

    [theme.breakpoints.up('sm')]: {
      borderRight: `1px solid ${theme.palette.care.grey[300]}`,
      borderBottom: 'none',
    },
  }),
  statLabels: (theme: Theme) => ({
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2.5),
    },
  }),
  statValues: (theme: Theme) => ({
    [theme.breakpoints.down('sm')]: {
      marginTop: theme.spacing(3),
    },
  }),
  noBorderRightDT: (theme: Theme) => ({
    [theme.breakpoints.up('sm')]: {
      borderRight: 'none',
    },
  }),
  noBorderRight: {
    borderRight: 'none',
  },
  addBorderBottom: (theme: Theme) => ({
    borderBottom: `1px solid ${theme.palette.care.grey[300]}`,

    [theme.breakpoints.up('sm')]: {
      borderBottom: 'none',
    },
  }),
} as const;

const numberFixTo = (value: number | null) => {
  return value?.toFixed(1);
};

interface SeoStatsProps {
  title: SEOSegmentationCaregiverStats['title'];
  totalProviders: SEOSegmentationCaregiverStats['totalProviders'];
  avgYearsOfExperience: SEOSegmentationCaregiverStats['avgYearsOfExperience'];
  totalReviewCount: SEOSegmentationCaregiverStats['totalReviewCount'];
  avgReviewRating: SEOSegmentationCaregiverStats['avgReviewRating'];
}

const SeoStats = ({
  title,
  totalProviders,
  avgYearsOfExperience,
  totalReviewCount,
  avgReviewRating,
}: SeoStatsProps) => {
  const showAvgYearsOfExp = avgYearsOfExperience !== null && avgYearsOfExperience > 0;
  const showTotalReviewCount = totalReviewCount !== null && totalReviewCount > 0;
  const showAvgReviewRating = avgReviewRating !== null && avgReviewRating > 0;

  return (
    <Grid item xs={12}>
      <Box sx={classes.careByNumContainer}>
        <Typography variant="h2" color="primary" sx={classes.careByNumText}>
          {title}
        </Typography>
        <Table aria-label="care numbers">
          <TableBody sx={classes.tableBody}>
            <TableRow sx={classes.statRow}>
              <TableCell
                sx={[
                  classes.statCell,
                  (showTotalReviewCount || showAvgReviewRating) && classes.addBorderBottom,
                ]}>
                <Typography variant="h2" color="primary">
                  {formatNumToString(totalProviders)}
                </Typography>
                <Typography variant="body3" color="secondary" sx={classes.statLabels}>
                  providers
                </Typography>
              </TableCell>
            </TableRow>
            {showAvgYearsOfExp && (
              <TableRow sx={classes.statRow}>
                <TableCell
                  sx={[
                    classes.statCell,
                    (showTotalReviewCount || showAvgReviewRating) && classes.avgExpStat,
                    !(showTotalReviewCount || showAvgReviewRating) && classes.noBorderRight,
                  ]}>
                  <Typography variant="h2" color="primary">
                    {numberFixTo(avgYearsOfExperience)} yrs
                  </Typography>
                  <Typography variant="body3" color="secondary" sx={classes.statLabels}>
                    avg. experience
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {showTotalReviewCount && (
              <TableRow sx={classes.statRow}>
                <TableCell sx={[classes.statCell, !showAvgReviewRating && classes.noBorderRight]}>
                  <Typography variant="h2" color="primary" sx={classes.statValues}>
                    {formatNumToString(totalReviewCount)}
                  </Typography>
                  <Typography variant="body3" color="secondary">
                    member reviews
                  </Typography>
                </TableCell>
              </TableRow>
            )}
            {showAvgReviewRating && (
              <TableRow sx={classes.statRow}>
                <TableCell
                  sx={[
                    classes.statCell,
                    showTotalReviewCount && classes.noBorderRight,
                    !showTotalReviewCount && classes.noBorderRightDT,
                  ]}>
                  <Typography variant="h2" color="primary" sx={classes.statValues}>
                    {numberFixTo(avgReviewRating)} / 5
                  </Typography>
                  <Typography variant="body3" color="secondary">
                    avg. rating
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>
    </Grid>
  );
};

export default SeoStats;
